import React, { useCallback, useEffect, useState } from 'react';
import useFiltersState from '../reporting_utils/useFiltersState';
import Layout from '../reporting_utils/Layout';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '@reach/tabs';
import '@reach/tabs/styles.css';
import Dashboard from './Dashboard';
import Trends from './Trends';
import CallAnalysis from './CallAnalysis';
import MarketingBreakdown from './MarketingBreakdown';
import AppointmentConversion from './AppointmentConversion';
import { Common } from '../../utils/common';
import { getFilterValues } from '../reporting_utils/misc';
import moment from 'moment';
import PropTypes from 'prop-types';

const pageLoadingFrame = window.pageLoadingFrame;

const defaultFilterState = {};

const maxDate = moment().subtract(1, 'month').endOf('month').format('YYYYMMDD');

function PerformanceSummary({ controllerName }) {
  const [filters, setFilters] = useFiltersState(defaultFilterState, undefined, undefined, maxDate);
  const [data, setData] = useState({
    results: [
      {
        start_date: '',
        end_date: '',
        total_calls: 0,
        total_calls_delta: 0,
        leadscore_calls: 0,
        leadscore_calls_delta: 0,
        prospect_calls: 0,
        prospect_calls_delta: 0,
        bookable_calls: 0,
        bookable_calls_delta: 0,
        appt_calls: 0,
        appt_calls_delta: 0,
        noappt_calls: 0,
        noappt_calls_delta: 0,
        appt_conversion_rate: 0,
        appt_conversion_rate_delta: 0,
        alerted_calls: 0,
        alerted_calls_delta: 0,
        connected_calls: 0,
        connected_calls_delta: 0,
        busy_calls: 0,
        busy_calls_delta: 0,
        no_answer_calls: 0,
        no_answer_calls_delta: 0,
        abandoned_calls: 0,
        abandoned_calls_delta: 0,
      },
    ],
    industry: null,
    campaigns: [],
    employees: [],
    calltypes: [],
  });

  const fetchData = useCallback(async () => {
    if (!filters.domains) {
      return;
    }
    pageLoadingFrame('show');
    try {
      const res = await fetch('/performance_summary', {
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(getFilterValues(filters)),
      });
      const data = await res.json();
      setData(data);
    } catch (error) {
      console.error(error);
      Common.notify('Failed to fetch report data', 'error');
    } finally {
      pageLoadingFrame('hide');
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const currentMonth = moment(
    `${filters.end_date.slice(0, 4)}-${filters.end_date.slice(4, 6)}-${filters.end_date.slice(
      6,
      8
    )}`
  )
    .startOf('month')
    .format('YYYY-MM-DD');

  const currentYear = filters.end_date.slice(0, 4);
  const previousYear = (parseInt(currentYear) - 1).toString();

  const currentMonthConversionRateYou = data.results.at(-1).appt_conversion_rate ?? 0;
  const priorMonthConversionRateYou = data.results.at(-2)?.appt_conversion_rate ?? 0;
  const sixMonthAverageConversionYou = Math.round(
    data.results.slice(-6).reduce((acc, item) => {
      return acc + item.appt_conversion_rate;
    }, 0) / 6
  );

  const sixMonthAvgProspectCallsYou =
    data.results.slice(-6).reduce((acc, item) => {
      return acc + item.prospect_calls;
    }, 0) / 6;
  const sixMonthAvgQualifiedCallsYou =
    data.results.slice(-6).reduce((acc, item) => {
      return acc + item.leadscore_calls;
    }, 0) / 6;
  const sixMonthAvgProspectRateYou = Math.round(
    (sixMonthAvgProspectCallsYou / sixMonthAvgQualifiedCallsYou) * 100
  );

  let currentMonthConversionRateIndustry = 0;
  let priorMonthConversionRateIndustry = 0;
  let sixMonthAverageConversionIndustry = 0;
  let sixMonthAvgProspectCallsIndustry = 0;
  let sixMonthAvgQualifiedCallsIndustry = 0;
  let sixMonthAvgProspectRateIndustry = 0;

  if (data.industry) {
    currentMonthConversionRateIndustry = data.industry.at(-1).appt_conversion_rate;
    priorMonthConversionRateIndustry = data.industry.at(-2)?.appt_conversion_rate ?? 0;
    sixMonthAverageConversionIndustry = Math.round(
      data.industry.slice(-6).reduce((acc, item) => {
        return acc + item.appt_conversion_rate;
      }, 0) / 6
    );

    sixMonthAvgProspectCallsIndustry =
      data.industry.slice(-6).reduce((acc, item) => {
        return acc + item.prospect_calls;
      }, 0) / 6;
    sixMonthAvgQualifiedCallsIndustry =
      data.industry.slice(-6).reduce((acc, item) => {
        return acc + item.leadscore_calls;
      }, 0) / 6;
    sixMonthAvgProspectRateIndustry = Math.round(
      (sixMonthAvgProspectCallsIndustry / sixMonthAvgQualifiedCallsIndustry) * 100
    );
  }

  return (
    <Layout
      title='Performance Summary'
      fetchData={fetchData}
      filters={filters}
      setFilters={setFilters}
      defaultFilterState={defaultFilterState}
      datePickerMode='singledate'
      controllerName={controllerName}
      reportSchedulerTimeframeOptions={['PRIOR_MONTH']}
    >
      <Tabs>
        <TabList style={{ marginBottom: 20 }}>
          <Tab>Dashboard</Tab>
          <Tab>Monthly & Quarterly Trends</Tab>
          <Tab>Call Analysis</Tab>
          <Tab>Marketing Breakdown</Tab>
          <Tab>Appointment Conversion Breakdown</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <Dashboard
              data={data}
              sixMonthAverageConversionYou={sixMonthAverageConversionYou}
              sixMonthAverageConversionIndustry={sixMonthAverageConversionIndustry}
              currentYear={currentYear}
              previousYear={previousYear}
            />
          </TabPanel>
          <TabPanel>
            <Trends data={data} currentYear={currentYear} previousYear={previousYear} />
          </TabPanel>
          <TabPanel>
            <CallAnalysis
              data={data}
              conversionRates={{
                currentMonthConversionRateYou,
                priorMonthConversionRateYou,
                sixMonthAverageConversionYou,
                currentMonthConversionRateIndustry,
                priorMonthConversionRateIndustry,
                sixMonthAverageConversionIndustry,
              }}
              prospectRates={{
                sixMonthAvgProspectRateYou,
                sixMonthAvgProspectRateIndustry,
              }}
              results={data.results}
              industry={data.industry}
              currentYear={currentYear}
            />
          </TabPanel>
          <TabPanel>
            <MarketingBreakdown tableData={data.campaigns} currentMonth={currentMonth} />
          </TabPanel>
          <TabPanel>
            <AppointmentConversion tableData={data.employees} currentMonth={currentMonth} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Layout>
  );
}

PerformanceSummary.propTypes = {
  controllerName: PropTypes.string.isRequired,
};

export default PerformanceSummary;
